import { Dispatch, SetStateAction, useEffect } from 'react';
import { theme } from '@frontend/theme';

type Props = {
  animationProgress: number;
  lottieRef: React.RefObject<any>;
  selectedAnimationIndex: number;
  setAnimationProgress: Dispatch<SetStateAction<number>>;
  shorts: any[];
};

export const ProgressBar = ({
  animationProgress,
  lottieRef,
  selectedAnimationIndex,
  setAnimationProgress,
  shorts,
}: Props) => {
  const shortsCount = shorts.length;

  useEffect(() => {
    if (!lottieRef.current) return;

    const interval = setInterval(() => {
      const anim = lottieRef.current;
      if (anim) {
        const currentFrame = anim.currentFrame || 0;
        const totalFrames = anim.totalFrames || 1;

        const progress = Math.min((currentFrame / totalFrames) * 100, 100);
        setAnimationProgress(progress);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [lottieRef, selectedAnimationIndex, setAnimationProgress]);

  return (
    <div
      css={{
        position: 'absolute',
        top: theme.spacing(1.5),
        left: theme.spacing(1.5),
        display: 'flex',
        gap: 6,
        width: 'calc(100% - 24px)',
      }}
    >
      {shorts.map((_, index) => {
        // Calculate fill percentage for each bar based on current progress
        let fillPercentage = 0;
        if (index < selectedAnimationIndex) {
          // Completed animations - 100% filled
          fillPercentage = 100;
        } else if (index === selectedAnimationIndex) {
          // Current animation - use actual progress (clamped to prevent overflow)
          fillPercentage = Math.min(animationProgress, 100);
        }
        // Future animations remain at 0%

        return (
          <div
            key={index}
            css={{
              height: 4,
              borderRadius: theme.borderRadius.small,
              background: 'rgb(171, 181, 197, 0.35)',
              width: `${100 / shortsCount}%`,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              css={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: `${fillPercentage}%`,
                background: theme.colors.white,
                transition: 'width 0.05s linear',
              }}
            />
          </div>
        );
      })}
    </div>
  );
};
